/**
 * Vendor Portal Services Module
 * SDK services for vendor self-service portal operations.
 *
 * These services allow vendors to securely manage their own data through
 * dedicated endpoints that validate vendor ownership for all operations.
 *
 * Security:
 * - All requests require JWT authentication with "Vendor" role
 * - Vendor ID is sent via X-Vendor-ID header
 * - Server validates vendor ownership for all operations
 * - Vendors can only access/modify their own data
 */
// Base service with vendor context support
export { VendorPortalBaseService } from './VendorPortalBaseService';
// Profile management
export { VendorPortalProfileService } from './VendorPortalProfileService';
// Document management
export { VendorPortalDocumentService } from './VendorPortalDocumentService';
// Payment management (CRUD)
export { VendorPortalPaymentService } from './VendorPortalPaymentService';
// Contact management (CRUD)
export { VendorPortalContactService } from './VendorPortalContactService';
// Activity viewing (read-only)
export { VendorPortalActivityService } from './VendorPortalActivityService';
// Product management
export { VendorPortalProductService } from './VendorPortalProductService';
// Product type reference data (read-only)
export { VendorPortalProductTypeService } from './VendorPortalProductTypeService';
// Product variant management
export { VendorPortalProductVariantService } from './VendorPortalProductVariantService';
// Product media management
export { VendorPortalProductMediaService } from './VendorPortalProductMediaService';
// Product analytics (read-only)
export { VendorPortalProductAnalyticsService } from './VendorPortalProductAnalyticsService';
// Rating viewing (read-only)
export { VendorPortalRatingService } from './VendorPortalRatingService';
// Response management (CRUD for vendor responses to ratings)
export { VendorPortalResponseService } from './VendorPortalResponseService';
// Flag viewing (read-only)
export { VendorPortalFlagService } from './VendorPortalFlagService';
// Event registration management
export { VendorPortalEventRegistrationService } from './VendorPortalEventRegistrationService';
// Product event limit management
export { VendorPortalProductEventLimitService } from './VendorPortalProductEventLimitService';
// Location management
export { VendorPortalLocationService } from './VendorPortalLocationService';
// Term management
export { VendorPortalTermService } from './VendorPortalTermService';
// Donation viewing (read-only)
export { VendorPortalDonationService } from './VendorPortalDonationService';
// Social media management (CRUD)
export { VendorPortalSocialMediaService } from './VendorPortalSocialMediaService';
// Product add-on management (CRUD)
export { VendorPortalProductAddOnService } from './VendorPortalProductAddOnService';
// Product add-on category management (CRUD)
export { VendorPortalProductAddOnCategoryService } from './VendorPortalProductAddOnCategoryService';
// Product variant category management (read-only, global categories)
export { VendorPortalProductVariantCategoryService } from './VendorPortalProductVariantCategoryService';
// Order management (read-only, vendor-scoped)
export { VendorPortalOrderService } from './VendorPortalOrderService';
// Models
export * from './models';
// Import for factory function
import { VendorPortalProfileService } from './VendorPortalProfileService';
import { VendorPortalDocumentService } from './VendorPortalDocumentService';
import { VendorPortalPaymentService } from './VendorPortalPaymentService';
import { VendorPortalContactService } from './VendorPortalContactService';
import { VendorPortalActivityService } from './VendorPortalActivityService';
import { VendorPortalProductService } from './VendorPortalProductService';
import { VendorPortalProductTypeService } from './VendorPortalProductTypeService';
import { VendorPortalProductVariantService } from './VendorPortalProductVariantService';
import { VendorPortalProductMediaService } from './VendorPortalProductMediaService';
import { VendorPortalProductAnalyticsService } from './VendorPortalProductAnalyticsService';
import { VendorPortalRatingService } from './VendorPortalRatingService';
import { VendorPortalResponseService } from './VendorPortalResponseService';
import { VendorPortalFlagService } from './VendorPortalFlagService';
import { VendorPortalEventRegistrationService } from './VendorPortalEventRegistrationService';
import { VendorPortalLocationService } from './VendorPortalLocationService';
import { VendorPortalTermService } from './VendorPortalTermService';
import { VendorPortalDonationService } from './VendorPortalDonationService';
import { VendorPortalProductEventLimitService } from './VendorPortalProductEventLimitService';
import { VendorPortalSocialMediaService } from './VendorPortalSocialMediaService';
import { VendorPortalProductAddOnService } from './VendorPortalProductAddOnService';
import { VendorPortalProductAddOnCategoryService } from './VendorPortalProductAddOnCategoryService';
import { VendorPortalProductVariantCategoryService } from './VendorPortalProductVariantCategoryService';
import { VendorPortalOrderService } from './VendorPortalOrderService';
/**
 * Create all vendor portal services
 * @param http - HttpClient instance
 * @param config - Optional service configuration
 * @returns Object containing all vendor portal services
 */
export function createVendorPortalServices(http, config) {
    const profile = new VendorPortalProfileService(http, config);
    const documents = new VendorPortalDocumentService(http, config);
    const locations = new VendorPortalLocationService(http, config);
    const payments = new VendorPortalPaymentService(http, config);
    const contacts = new VendorPortalContactService(http, config);
    const activity = new VendorPortalActivityService(http, config);
    const products = new VendorPortalProductService(http, config);
    const productTypes = new VendorPortalProductTypeService(http, config);
    const productVariants = new VendorPortalProductVariantService(http, config);
    const productMedia = new VendorPortalProductMediaService(http, config);
    const productAnalytics = new VendorPortalProductAnalyticsService(http, config);
    const ratings = new VendorPortalRatingService(http, config);
    const responses = new VendorPortalResponseService(http, config);
    const flags = new VendorPortalFlagService(http, config);
    const eventRegistrations = new VendorPortalEventRegistrationService(http, config);
    const productEventLimits = new VendorPortalProductEventLimitService(http, config);
    const terms = new VendorPortalTermService(http, config);
    const donations = new VendorPortalDonationService(http, config);
    const socialMedia = new VendorPortalSocialMediaService(http, config);
    const productAddOns = new VendorPortalProductAddOnService(http, config);
    const productAddOnCategories = new VendorPortalProductAddOnCategoryService(http, config);
    const productVariantCategories = new VendorPortalProductVariantCategoryService(http, config);
    const orders = new VendorPortalOrderService(http, config);
    return {
        profile,
        documents,
        locations,
        payments,
        contacts,
        activity,
        products,
        productTypes,
        productVariants,
        productMedia,
        productAnalytics,
        ratings,
        responses,
        flags,
        eventRegistrations,
        productEventLimits,
        terms,
        donations,
        socialMedia,
        productAddOns,
        productAddOnCategories,
        productVariantCategories,
        orders,
        /**
         * Set vendor ID on the HttpClient.
         * This sets the X-Vendor-ID header globally for ALL requests made through the SDK.
         * This is the recommended way to set vendor context after authentication.
         */
        setVendorId: (vendorId) => {
            // Set on HttpClient for global header injection
            http.setVendorId(vendorId);
            // Also set on individual services for backward compatibility
            profile.setVendorId(vendorId);
            documents.setVendorId(vendorId);
            locations.setVendorId(vendorId);
            payments.setVendorId(vendorId);
            contacts.setVendorId(vendorId);
            activity.setVendorId(vendorId);
            products.setVendorId(vendorId);
            productTypes.setVendorId(vendorId);
            productVariants.setVendorId(vendorId);
            productMedia.setVendorId(vendorId);
            productAnalytics.setVendorId(vendorId);
            ratings.setVendorId(vendorId);
            responses.setVendorId(vendorId);
            flags.setVendorId(vendorId);
            eventRegistrations.setVendorId(vendorId);
            productEventLimits.setVendorId(vendorId);
            terms.setVendorId(vendorId);
            donations.setVendorId(vendorId);
            socialMedia.setVendorId(vendorId);
            productAddOns.setVendorId(vendorId);
            productAddOnCategories.setVendorId(vendorId);
            productVariantCategories.setVendorId(vendorId);
            orders.setVendorId(vendorId);
        },
    };
}
//# sourceMappingURL=index.js.map